import React from 'react';

import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  LinearProgress,
  Link,
  Typography,
  useTheme,
} from '@mui/material';
import {
  BiX,
  BiMap,
  BiTime,
  BiPhone,
  BiCar,
  BiCheck,
  BiNote,
  BiReceipt,
  BiChevronDown,
  BiChevronUp,
  BiLinkExternal,
  BiRefresh,
  BiLockAlt,
  BiCalendar,
} from 'react-icons/bi';
import { toast } from 'react-toastify';

import PhotoLightbox from '@components/PhotoLightbox/PhotoLightbox';
import type { LightboxPhoto } from '@components/PhotoLightbox/PhotoLightbox';
import { getHumanReadableLabel } from '@services/vehicleImagesService';
import {
  fetchServiceStopConfirmLink,
  resendServiceStopConfirmationSMS,
} from '../services/confirmationLinkService';

// ── Types ------------------------------------------------------------------ //

interface WorkflowStep {
  id: string;
  name?: string;
  label?: string;
  type?: string;
  required?: boolean;
  config?: { private?: boolean };
  steps?: WorkflowStep[];
}

interface VehiclePhoto {
  id: string;
  url?: string | null;
  name: string;
  step_id: string;
  status: string;
  createdat: string;
  workflow_id: number;
}

interface ServiceOrder {
  id: number;
  vehicle_vin?: string | null;
  vehicle_make?: string | null;
  vehicle_model?: string | null;
  vehicle_year?: number | null;
  vehicle_color?: string | null;
  service_description: string;
  estimated_minutes?: number | null;
  status: string;
  started_at?: string | null;
  completed_at?: string | null;
  workflow_data: Record<string, unknown>;
  vehiclephotos: VehiclePhoto[];
}

interface ServiceStopDetail {
  customer_name?: string | null;
  customer_phone?: string | null;
  service_description?: string | null;
  parking_location?: string | null;
  gate_code?: string | null;
  key_handoff_method?: string | null;
  special_instructions?: string | null;
  service_window?: string | null;
  estimated_service_minutes?: number | null;
  ro_reference?: string | null;
  is_fleet_stop: boolean;
  fleet_vehicle_count?: number | null;
  service_started_at?: string | null;
  service_completed_at?: string | null;
  skip_reason_code?: string | null;
  skip_notes?: string | null;
  hash_expires_at?: string | null;
}

interface Stop {
  id: number;
  stop_order: number;
  status: string;
  address?: string | null;
  notes?: string | null;
  geofence_arrived_at?: string | null;
  geofence_departed_at?: string | null;
  driver_confirmed_at?: string | null;
  drive_time_seconds?: number | null;
  dwell_time_seconds?: number | null;
  location?: {
    id: number;
    name?: string | null;
    address?: string | null;
  } | null;
  service_stop_detail: ServiceStopDetail[];
  service_orders: ServiceOrder[];
}

interface StopDetailDrawerProps {
  open: boolean;
  stop: Stop | null;
  workflowSteps: WorkflowStep[] | null;
  totalStops: number;
  onClose: () => void;
}

// ── Helpers ----------------------------------------------------------------- //

const stopStatusColors: Record<string, string> = {
  pending_confirmation: 'warning',
  confirmed: 'info',
  assigned: 'info',
  in_transit_to: 'warning',
  arrived: 'primary',
  in_service: 'primary',
  service_in_progress: 'primary',
  completed: 'success',
  skipped: 'error',
};

const stopStatusLabels: Record<string, string> = {
  pending_confirmation: 'Awaiting Confirmation',
};

const orderStatusColors: Record<string, string> = {
  pending: 'default',
  in_progress: 'warning',
  completed: 'success',
  skipped: 'error',
};

const formatStatus = (status: string) =>
  status.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

const formatClockTime = (iso: string) =>
  new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

const formatShortDate = (iso: string) =>
  new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });

/** "+Xm" delta between two timeline events, or null when not meaningful */
const formatGap = (prevIso: string, iso: string) => {
  const mins = Math.round((+new Date(iso) - +new Date(prevIso)) / 60000);
  return mins > 0 ? `+${mins}m` : null;
};

/** Lowercased color name if the browser recognizes it as a CSS color (for the swatch) */
const toCssColor = (name?: string | null) => {
  if (!name) return null;
  const c = name.toLowerCase();
  return typeof CSS !== 'undefined' && CSS.supports?.('color', c) ? c : null;
};

const formatDuration = (seconds?: number | null) => {
  if (!seconds) return null;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
};

/** Flatten nested workflow steps into a flat list of leaf steps */
const flattenSteps = (steps: WorkflowStep[]): WorkflowStep[] => {
  const result: WorkflowStep[] = [];
  for (const step of steps) {
    if (step.steps?.length) {
      result.push(...flattenSteps(step.steps));
    } else {
      result.push(step);
    }
  }
  return result;
};

/** Get top-level groups from workflow steps (groups have child steps) */
const getWorkflowGroups = (steps: WorkflowStep[]): { group: WorkflowStep; leafSteps: WorkflowStep[] }[] => {
  return steps.map(step => ({
    group: step,
    leafSteps: step.steps?.length ? flattenSteps(step.steps) : [step],
  }));
};

const DRAWER_WIDTH = 460;

// ── Component --------------------------------------------------------------- //

const StopDetailDrawer: React.FC<StopDetailDrawerProps> = ({
  open,
  stop,
  workflowSteps,
  totalStops,
  onClose,
}) => {
  const theme = useTheme();
  // null = "auto": derive the default expansion (the active group) at render time
  const [expandedGroups, setExpandedGroups] = React.useState<Set<string> | null>(null);
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxPhotos, setLightboxPhotos] = React.useState<LightboxPhoto[]>([]);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);
  const [confirmLinkBusy, setConfirmLinkBusy] = React.useState(false);
  // Post-refresh "also text the customer?" inline prompt.
  const [smsPromptOpen, setSmsPromptOpen] = React.useState(false);

  // Reset to auto-expansion when the stop changes
  React.useEffect(() => {
    setExpandedGroups(null);
    setSmsPromptOpen(false);
  }, [stop?.id, workflowSteps]);

  if (!stop) return null;

  const detail = stop.service_stop_detail?.[0];
  const orders = stop.service_orders ?? [];
  const loc = stop.location;
  const address = loc?.address || stop.address || 'No address';
  const isDealershipStop = stop.stop_order === 1 || stop.stop_order === totalStops;
  const isCompleted = stop.status === 'completed';
  const showConfirmActions = !isDealershipStop && stop.status === 'pending_confirmation';
  const customerFirstName = detail?.customer_name?.trim().split(/\s+/)[0] || null;

  // The confirmation window is per-stop (stateless hashes, gated only by
  // hash_expires_at) — when it lapses, every link ever texted for this stop
  // stops working until something refreshes it.
  const linkExpiresAt = detail?.hash_expires_at ? new Date(detail.hash_expires_at) : null;
  const linkExpired = linkExpiresAt !== null && +linkExpiresAt < Date.now();

  // Time on site: prefer the geofence dwell, fall back to service start → complete
  const onSiteMinutes = stop.dwell_time_seconds
    ? Math.round(stop.dwell_time_seconds / 60)
    : detail?.service_started_at && detail?.service_completed_at
      ? Math.round((+new Date(detail.service_completed_at) - +new Date(detail.service_started_at)) / 60000)
      : null;

  let estimateDeltaText: string | null = null;
  if (onSiteMinutes != null && detail?.estimated_service_minutes) {
    const delta = detail.estimated_service_minutes - onSiteMinutes;
    estimateDeltaText =
      delta === 0 ? 'on estimate' : delta > 0 ? `${delta} min under estimate` : `${-delta} min over estimate`;
  }

  // Refresh the confirmation window WITHOUT texting anyone: fetching a link
  // re-stamps hash_expires_at server-side (generateGuestLink's side effect),
  // and links being stateless means the customer's original SMS link starts
  // working again too. The SMS is a separate, confirmed step (smsPromptOpen).
  const handleRefreshConfirmation = async () => {
    setConfirmLinkBusy(true);
    try {
      const url = await fetchServiceStopConfirmLink(stop.id);
      if (url) {
        toast.success("Confirmation link refreshed — the customer's original link works again");
        setSmsPromptOpen(true);
      } else {
        toast.error('Failed to refresh the confirmation link');
      }
    } catch (err) {
      console.error('Failed to refresh confirmation link:', err);
      toast.error('Failed to refresh the confirmation link');
    } finally {
      setConfirmLinkBusy(false);
    }
  };

  const handleResendConfirmSMS = async () => {
    setConfirmLinkBusy(true);
    try {
      await resendServiceStopConfirmationSMS(stop.id);
    } finally {
      setConfirmLinkBusy(false);
      setSmsPromptOpen(false);
    }
  };

  const handleOpenConfirmForm = async () => {
    setConfirmLinkBusy(true);
    // Open the tab synchronously in the click gesture so popup blockers
    // don't eat it, then point it at the fetched URL.
    const win = window.open('', '_blank');
    try {
      const url = await fetchServiceStopConfirmLink(stop.id);
      if (url && win) {
        win.location.href = url;
      } else {
        win?.close();
        if (!url) toast.error('Failed to generate confirmation link');
      }
    } catch (err) {
      win?.close();
      console.error('Failed to open confirmation form:', err);
      toast.error('Failed to generate confirmation link');
    } finally {
      setConfirmLinkBusy(false);
    }
  };

  // Collect all photos across service orders
  const allPhotos: (VehiclePhoto & { orderIndex: number })[] = [];
  orders.forEach((order, orderIdx) => {
    order.vehiclephotos?.forEach(p => {
      if (p.url) allPhotos.push({ ...p, orderIndex: orderIdx });
    });
  });

  // Collect all workflow data across orders.
  //
  // The driver-app's generic workflow interceptor (writeServiceOrderWorkflowToRealm)
  // writes step values into a nested `formValues` bucket:
  //   { formValues: { 'confirm-vin': '...', 'some-text-step': '...' }, completedAt: '...' }
  // Flatten formValues entries up to the top-level so the existing
  // step-id-keyed lookups below (completedStepIds set construction, per-step
  // value rendering) work without needing to know about the nested shape.
  const allWorkflowData: Record<string, unknown> = {};
  orders.forEach(order => {
    if (order.workflow_data && typeof order.workflow_data === 'object') {
      const wd = order.workflow_data as Record<string, unknown>;
      Object.assign(allWorkflowData, wd);
      if (wd.formValues && typeof wd.formValues === 'object') {
        Object.assign(allWorkflowData, wd.formValues as Record<string, unknown>);
      }
    }
  });

  // Photo/signature/document-scan steps don't write to workflow_data on the
  // driver side — their data lives in vehiclephotos. Treat a leaf step as
  // completed if it has at least one matching photo. 'damages' is excluded
  // because it's a free-form bucket, not a workflow leaf step.
  const photoStepIds = new Set(
    allPhotos
      .filter(p => p.step_id && p.step_id !== 'damages')
      .map(p => p.step_id)
  );

  const workflowGroups = workflowSteps ? getWorkflowGroups(workflowSteps) : [];
  const allLeafSteps = workflowSteps ? flattenSteps(workflowSteps) : [];
  const completedStepIds = new Set<string>([
    ...Object.keys(allWorkflowData),
    ...photoStepIds,
  ]);

  // A step counts toward the section's denominator if it's required OR if the
  // tech actually filled it in. Optional steps that are still empty
  // (e.g. "Damage Photos" with no captures) don't inflate the count.
  const stepCountsTowardTotal = (s: WorkflowStep) =>
    s.required === true || completedStepIds.has(s.id);
  const countedLeafSteps = allLeafSteps.filter(stepCountsTowardTotal);
  const completedCount = countedLeafSteps.filter(s => completedStepIds.has(s.id)).length;
  const totalStepCount = countedLeafSteps.length;
  const allStepsComplete = totalStepCount > 0 && completedCount === totalStepCount;

  // Groups start collapsed with the "active" group (first with incomplete
  // counted steps) auto-expanded; fully complete stops expand the first group.
  const activeGroup =
    workflowGroups.find(({ leafSteps }) =>
      leafSteps.filter(stepCountsTowardTotal).some(s => !completedStepIds.has(s.id))
    ) ?? workflowGroups[0];
  const defaultExpanded = new Set<string>(activeGroup ? [activeGroup.group.id] : []);
  const effectiveExpanded = expandedGroups ?? defaultExpanded;

  // Any photo whose step_id isn't in the current workflow's leaf-step set
  // (e.g. 'damages' or stale step_ids from an edited workflow) is bucketed
  // here so it still surfaces somewhere in the drawer.
  const leafStepIdSet = new Set(allLeafSteps.map(s => s.id));
  const orphanedByStepId = new Map<string, (VehiclePhoto & { orderIndex: number })[]>();
  for (const photo of allPhotos) {
    if (!leafStepIdSet.has(photo.step_id)) {
      const key = photo.step_id || 'other';
      const bucket = orphanedByStepId.get(key) ?? [];
      bucket.push(photo);
      orphanedByStepId.set(key, bucket);
    }
  }

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev ?? defaultExpanded);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  const openLightbox = (photos: VehiclePhoto[], startIndex: number) => {
    setLightboxPhotos(
      photos
        .filter(p => p.url)
        .map(p => ({
          url: p.url!,
          label: getHumanReadableLabel(p.step_id === 'damages' ? p.name : p.step_id),
        }))
    );
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  };

  // ── Timeline Events ── //
  const timelineEvents: { label: string; at: string }[] = [];
  if (stop.driver_confirmed_at) {
    timelineEvents.push({ label: 'Confirmed', at: stop.driver_confirmed_at });
  }
  if (stop.geofence_arrived_at) {
    timelineEvents.push({ label: 'Arrived at geofence', at: stop.geofence_arrived_at });
  }
  if (detail?.service_started_at) {
    timelineEvents.push({ label: 'Service started', at: detail.service_started_at });
  }
  orders.forEach((order, i) => {
    if (order.started_at) {
      timelineEvents.push({
        label: orders.length > 1 ? `Order ${i + 1} started` : 'Inspection started',
        at: order.started_at,
      });
    }
    if (order.completed_at) {
      timelineEvents.push({
        label: orders.length > 1 ? `Order ${i + 1} completed` : 'Inspection completed',
        at: order.completed_at,
      });
    }
  });
  if (detail?.service_completed_at) {
    timelineEvents.push({ label: 'Service completed', at: detail.service_completed_at });
  }
  if (stop.geofence_departed_at) {
    timelineEvents.push({ label: 'Departed geofence', at: stop.geofence_departed_at });
  }
  timelineEvents.sort((a, b) => +new Date(a.at) - +new Date(b.at));
  const timelineDate = timelineEvents.length > 0 ? formatShortDate(timelineEvents[0].at) : null;

  return (
    <>
      <Drawer
        anchor='right'
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: DRAWER_WIDTH,
            bgcolor: 'background.paper',
          },
        }}
      >
        {/* ── Header ── */}
        <Box sx={{
          px: 2.5,
          py: 2,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}>
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: isDealershipStop
                ? '#3b82f6'
                : isCompleted
                  ? 'success.main'
                  : 'secondaryAccent.main',
              fontSize: 15,
              fontWeight: 700,
              mt: 0.25,
            }}
          >
            {!isDealershipStop && isCompleted ? <BiCheck size={20} /> : stop.stop_order}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant='subtitle1' sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {isDealershipStop
                ? (stop.stop_order === 1 ? 'Depart from Dealership' : 'Return to Dealership')
                : (detail?.customer_name || stop.notes || loc?.name || 'Unknown')
              }
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
              {/* Dealership bookends carry no meaningful status — no chip */}
              {!isDealershipStop ? (
                <Chip
                  label={stopStatusLabels[stop.status] || formatStatus(stop.status)}
                  size='small'
                  color={(stopStatusColors[stop.status] || 'default') as any}
                  sx={{ fontSize: 10, fontWeight: 600, height: 20 }}
                />
              ) : null}
              {isCompleted && onSiteMinutes != null ? (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <BiTime size={13} />
                  <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                    {onSiteMinutes} min
                  </Box>
                  on site
                </Typography>
              ) : detail?.estimated_service_minutes ? (
                <Typography variant='caption' color='text.secondary' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  <BiTime size={13} />
                  Est. ~{detail.estimated_service_minutes} min
                </Typography>
              ) : null}
            </Box>
          </Box>
          <IconButton size='small' onClick={onClose} sx={{ mt: -0.5, mr: -1 }}>
            <BiX size={20} />
          </IconButton>
        </Box>

        {/* ── Scrollable body ── */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>

          {/* ── Location & Contact ── */}
          <Box sx={{ px: 2.5, py: 2, bgcolor: 'background.default' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <BiMap size={16} color={theme.palette.text.secondary} style={{ marginTop: 2, flexShrink: 0 }} />
              <Box>
                {loc?.name ? (
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>{loc.name}</Typography>
                ) : null}
                <Typography variant='caption' color='text.secondary'>{address}</Typography>
              </Box>
            </Box>
            {detail?.customer_phone ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BiPhone size={16} color={theme.palette.text.secondary} />
                <Link
                  href={`tel:${detail.customer_phone.replace(/[^+\d]/g, '')}`}
                  variant='body2'
                  color='text.primary'
                  underline='hover'
                >
                  {detail.customer_phone}
                </Link>
              </Box>
            ) : null}
            {detail?.service_window ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BiCalendar size={16} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />
                <Typography variant='body2' color='text.secondary'>
                  Appointment: {detail.service_window}
                </Typography>
              </Box>
            ) : null}
            {detail?.ro_reference ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <BiReceipt size={16} color={theme.palette.text.secondary} style={{ flexShrink: 0 }} />
                <Typography variant='body2' color='text.secondary'>
                  RO #: {detail.ro_reference}
                </Typography>
              </Box>
            ) : null}
            {detail?.service_description ? (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                <BiNote size={16} color={theme.palette.text.secondary} style={{ marginTop: 2, flexShrink: 0 }} />
                <Typography variant='body2' color='text.secondary' sx={{ fontStyle: 'italic' }}>
                  {detail.service_description}
                </Typography>
              </Box>
            ) : null}
            {detail?.special_instructions ? (
              <Box sx={{ mt: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Special Instructions
                </Typography>
                <Typography variant='caption'>{detail.special_instructions}</Typography>
              </Box>
            ) : null}
            {(detail?.parking_location || detail?.gate_code || detail?.key_handoff_method) ? (
              <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {detail.parking_location ? (
                  <Typography variant='caption' color='text.secondary'>Parking: {detail.parking_location}</Typography>
                ) : null}
                {detail.gate_code ? (
                  <Typography variant='caption' color='text.secondary'>Gate: {detail.gate_code}</Typography>
                ) : null}
                {detail.key_handoff_method ? (
                  <Typography variant='caption' color='text.secondary'>Keys: {detail.key_handoff_method}</Typography>
                ) : null}
              </Box>
            ) : null}
          </Box>

          <Divider />

          {/* ── Customer Confirmation (dealer confirms on customer's behalf) ── */}
          {showConfirmActions ? (
            <>
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 0.5 }}>
                  Customer Confirmation
                </Typography>
                <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: linkExpired ? 0.75 : 1.5 }}>
                  This stop is waiting on {customerFirstName || 'the customer'} to confirm the
                  details. Confirm on their behalf, or send them the link.
                </Typography>
                {linkExpired ? (
                  <Typography variant='caption' sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: 'warning.main',
                    fontWeight: 600,
                    mb: 1.5,
                  }}>
                    <BiTime size={13} />
                    The customer's link expired {linkExpiresAt!.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} — refresh to reactivate it.
                  </Typography>
                ) : null}
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size='small'
                    variant='contained'
                    color='primary'
                    startIcon={<BiLinkExternal size={14} />}
                    onClick={handleOpenConfirmForm}
                    disabled={confirmLinkBusy}
                    sx={{ flex: 1 }}
                  >
                    Open Confirmation Form
                  </Button>
                  <Button
                    size='small'
                    variant={linkExpired ? 'contained' : 'outlined'}
                    color={linkExpired ? 'warning' : 'primary'}
                    startIcon={<BiRefresh size={15} />}
                    onClick={handleRefreshConfirmation}
                    disabled={confirmLinkBusy}
                    sx={{ flexShrink: 0 }}
                  >
                    Refresh Confirmation
                  </Button>
                </Box>
                {smsPromptOpen && detail?.customer_phone ? (
                  <Box sx={{
                    mt: 1,
                    px: 1.5,
                    py: 1,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}>
                    <Typography variant='caption' color='text.secondary' sx={{ flex: 1 }}>
                      Also text {customerFirstName || 'the customer'} the new link?
                    </Typography>
                    <Button
                      size='small'
                      variant='contained'
                      color='primary'
                      startIcon={<BiPhone size={13} />}
                      onClick={handleResendConfirmSMS}
                      disabled={confirmLinkBusy}
                      sx={{ flexShrink: 0 }}
                    >
                      Text Link
                    </Button>
                    <Button
                      size='small'
                      onClick={() => setSmsPromptOpen(false)}
                      disabled={confirmLinkBusy}
                      sx={{ flexShrink: 0 }}
                    >
                      Skip
                    </Button>
                  </Box>
                ) : null}
              </Box>
              <Divider />
            </>
          ) : null}

          {/* ── Timeline ── */}
          {timelineEvents.length > 0 ? (
            <>
              <Box sx={{ px: 2.5, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                    Timeline
                  </Typography>
                  {timelineDate ? (
                    <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
                      {timelineDate}
                    </Typography>
                  ) : null}
                </Box>
                <Box sx={{ ml: 0.5 }}>
                  {timelineEvents.map((evt, i) => {
                    const isLast = i === timelineEvents.length - 1;
                    const isTerminal = isLast && isCompleted;
                    const gap = i > 0 ? formatGap(timelineEvents[i - 1].at, evt.at) : null;
                    return (
                      <Box key={i} sx={{ position: 'relative', pl: 2.5, pb: isLast ? 0 : 1.5 }}>
                        <Box sx={{
                          position: 'absolute',
                          left: 3,
                          top: 5,
                          width: 7,
                          height: 7,
                          borderRadius: '50%',
                          bgcolor: isTerminal ? 'success.main' : 'grey.400',
                          zIndex: 1,
                        }} />
                        {!isLast ? (
                          <Box sx={{
                            position: 'absolute',
                            left: 6,
                            top: 12,
                            bottom: -4,
                            width: '1px',
                            bgcolor: 'divider',
                          }} />
                        ) : null}
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5 }}>
                          <Typography variant='caption' sx={{ flex: 1, fontWeight: isTerminal ? 600 : 400 }}>
                            {evt.label}
                          </Typography>
                          <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {formatClockTime(evt.at)}
                            {gap ? (
                              <Box component='span' sx={{ ml: 1, fontSize: 11, color: 'text.disabled' }}>
                                {gap}
                              </Box>
                            ) : null}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
                {onSiteMinutes != null || stop.drive_time_seconds ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1.75,
                    pt: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                  }}>
                    <BiTime size={14} color={theme.palette.text.disabled} />
                    <Typography variant='caption' color='text.secondary'>
                      {onSiteMinutes != null ? (
                        <>
                          <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                            {onSiteMinutes} min
                          </Box>
                          {' on site'}
                          {estimateDeltaText ? ` · ${estimateDeltaText}` : ''}
                        </>
                      ) : null}
                      {onSiteMinutes != null && stop.drive_time_seconds ? ' · ' : ''}
                      {stop.drive_time_seconds ? `${formatDuration(stop.drive_time_seconds)} drive` : ''}
                    </Typography>
                  </Box>
                ) : null}
              </Box>
              <Divider />
            </>
          ) : null}

          {/* ── Service Orders (vehicles) ── */}
          {orders.length > 0 && !isDealershipStop ? (
            <>
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1, display: 'block', mb: 1.5 }}>
                  {orders.length > 1 ? `Service Orders (${orders.length})` : 'Service Order'}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {orders.map((order) => {
                    const vehicleLabel = [order.vehicle_year, order.vehicle_make, order.vehicle_model]
                      .filter(Boolean)
                      .join(' ');
                    const swatchColor = toCssColor(order.vehicle_color);
                    return (
                      <Box
                        key={order.id}
                        sx={{
                          p: 1.75,
                          borderRadius: 2.5,
                          bgcolor: 'background.paper',
                          boxShadow: '0px 2px 12px 2px rgba(32,36,48,0.09)',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <BiCar size={18} color={theme.palette.text.disabled} />
                          {vehicleLabel ? (
                            <Typography variant='body2' noWrap sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}>
                              {vehicleLabel}
                            </Typography>
                          ) : (
                            <Typography variant='body2' noWrap sx={{ fontStyle: 'italic', color: 'text.disabled', flex: 1, minWidth: 0 }}>
                              Vehicle not identified
                            </Typography>
                          )}
                          <Chip
                            label={formatStatus(order.status)}
                            size='small'
                            color={(orderStatusColors[order.status] || 'default') as any}
                            sx={{ fontSize: 10, fontWeight: 600, height: 20 }}
                          />
                        </Box>
                        {order.vehicle_vin ? (
                          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75 }}>
                            <Typography variant='caption' color='text.secondary' sx={{ width: 34, flexShrink: 0 }}>
                              VIN
                            </Typography>
                            <Typography
                              variant='caption'
                              sx={{
                                fontFamily: '"JetBrains Mono", "Menlo", "Consolas", monospace',
                                letterSpacing: 0.4,
                                bgcolor: 'action.hover',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 1,
                                wordBreak: 'break-all',
                              }}
                            >
                              {order.vehicle_vin}
                            </Typography>
                          </Box>
                        ) : null}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                          {order.vehicle_color ? (
                            <Typography variant='caption' color='text.secondary' sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              bgcolor: 'action.hover',
                              borderRadius: 1,
                              px: 1,
                              py: 0.25,
                            }}>
                              {swatchColor ? (
                                <Box component='span' sx={{ width: 8, height: 8, borderRadius: 0.5, bgcolor: swatchColor, flexShrink: 0 }} />
                              ) : null}
                              {order.vehicle_color}
                            </Typography>
                          ) : null}
                          {order.service_description ? (
                            <Typography variant='caption' color='text.secondary' sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.25 }}>
                              {order.service_description}
                            </Typography>
                          ) : null}
                          {order.estimated_minutes ? (
                            <Typography variant='caption' color='text.secondary' sx={{ bgcolor: 'action.hover', borderRadius: 1, px: 1, py: 0.25 }}>
                              Est. {order.estimated_minutes} min
                            </Typography>
                          ) : null}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Divider />
            </>
          ) : null}

          {/* ── Workflow Progress ── */}
          {(workflowGroups.length > 0 || allPhotos.length > 0) && !isDealershipStop ? (
            <>
              <Box sx={{ px: 2.5, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: 1.5 }}>
                  <Typography variant='overline' sx={{ fontWeight: 600, color: 'text.secondary', letterSpacing: 1 }}>
                    Workflow Progress
                  </Typography>
                  <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {completedCount} of {totalStepCount} steps
                  </Typography>
                </Box>

                {showConfirmActions ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 1,
                    mb: 1.5,
                  }}>
                    <BiLockAlt size={13} color={theme.palette.text.disabled} style={{ marginTop: 2, flexShrink: 0 }} />
                    <Typography variant='caption' color='text.secondary'>
                      Steps unlock once this stop is confirmed.
                    </Typography>
                  </Box>
                ) : null}

                {allStepsComplete ? (
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    bgcolor: theme.palette.success.light + '30',
                    color: 'success.main',
                    borderRadius: 1.5,
                    px: 1.25,
                    py: 1,
                    mb: 1.5,
                  }}>
                    <BiCheck size={16} />
                    <Typography variant='caption' sx={{ fontWeight: 600, color: 'inherit' }}>
                      All {totalStepCount} steps complete
                    </Typography>
                  </Box>
                ) : totalStepCount > 0 ? (
                  <LinearProgress
                    variant='determinate'
                    value={(completedCount / totalStepCount) * 100}
                    sx={{
                      mb: 2,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: 'action.hover',
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        bgcolor: 'success.main',
                      },
                    }}
                  />
                ) : null}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {workflowGroups.map(({ group, leafSteps }) => {
                    const countedLeaves = leafSteps.filter(stepCountsTowardTotal);
                    const groupCompleted = countedLeaves.filter(s => completedStepIds.has(s.id)).length;
                    const groupTotal = countedLeaves.length;
                    const isExpanded = effectiveExpanded.has(group.id);
                    const isGroupComplete = groupCompleted === groupTotal && groupTotal > 0;
                    const isGroupActive = !isGroupComplete && activeGroup?.group.id === group.id;

                    return (
                      <Box key={group.id}>
                        {/* Group header */}
                        <Box
                          onClick={() => toggleGroup(group.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            py: 0.75,
                            px: 1,
                            borderRadius: 1,
                            cursor: 'pointer',
                            '&:hover': { bgcolor: 'action.hover' },
                          }}
                        >
                          {isGroupComplete ? (
                            <Box sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              bgcolor: 'success.main',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}>
                              <BiCheck size={14} color='#fff' />
                            </Box>
                          ) : (
                            <Box sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              border: '2px solid',
                              borderColor: isGroupActive
                                ? 'primary.main'
                                : groupCompleted > 0
                                  ? 'success.main'
                                  : 'grey.300',
                              boxShadow: isGroupActive ? `0 0 0 3px ${theme.palette.primary.main}20` : 'none',
                              flexShrink: 0,
                            }} />
                          )}
                          <Typography variant='body2' sx={{ fontWeight: 600, flex: 1, color: isGroupComplete ? 'text.secondary' : 'text.primary' }}>
                            {group.name || group.label || getHumanReadableLabel(group.id)}
                          </Typography>
                          <Typography variant='caption' color='text.secondary' sx={{ mr: 0.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {groupCompleted}/{groupTotal}
                          </Typography>
                          {isExpanded ? <BiChevronUp size={16} /> : <BiChevronDown size={16} />}
                        </Box>

                        {/* Group steps */}
                        {isExpanded ? (() => {
                          const visibleSteps = leafSteps.filter(s => !s.config?.private);
                          const stepsWithPhotos = visibleSteps.filter(s => allPhotos.some(p => p.step_id === s.id));
                          const stepsWithoutPhotos = visibleSteps.filter(s => !allPhotos.some(p => p.step_id === s.id));

                          return (
                            <Box sx={{ ml: 3.5, mb: 1 }}>
                              {/* 2-column grid of (label + photo) cells for steps that have photos */}
                              {stepsWithPhotos.length > 0 ? (
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', columnGap: 1, rowGap: 1.5, mt: 0.5 }}>
                                  {stepsWithPhotos.map(step => {
                                    const stepPhotos = allPhotos.filter(p => p.step_id === step.id && p.url);
                                    return (
                                      <Box key={step.id}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                          {stepPhotos.map((photo, pi) => (
                                            <Box
                                              key={photo.id}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openLightbox(stepPhotos, pi);
                                              }}
                                              sx={{
                                                width: '100%',
                                                aspectRatio: '4 / 3',
                                                borderRadius: 1.5,
                                                overflow: 'hidden',
                                                cursor: 'pointer',
                                                boxShadow: '0px 2px 12px 2px rgba(32,36,48,0.09)',
                                                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                                '&:hover': {
                                                  transform: 'translateY(-2px)',
                                                  boxShadow: '0px 4px 16px 2px rgba(32,36,48,0.19)',
                                                },
                                              }}
                                            >
                                              <img
                                                src={photo.url!}
                                                alt={photo.name}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                              />
                                            </Box>
                                          ))}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                                          <BiCheck size={13} color={theme.palette.grey[400]} style={{ flexShrink: 0 }} />
                                          <Typography variant='caption' color='text.secondary' noWrap>
                                            {step.name || step.label || getHumanReadableLabel(step.id)}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : null}

                              {/* Text rows for steps without photos — rendered below the photo grid */}
                              {stepsWithoutPhotos.length > 0 ? (
                                <Box sx={{ mt: stepsWithPhotos.length > 0 ? 1 : 0 }}>
                                  {stepsWithoutPhotos.map(step => {
                                    const stepDone = completedStepIds.has(step.id);
                                    const value = allWorkflowData[step.id];
                                    return (
                                      <Box key={step.id} sx={{ py: 0.5, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                        {stepDone ? (
                                          <BiCheck size={15} color={theme.palette.grey[400]} style={{ marginTop: 2, flexShrink: 0 }} />
                                        ) : (
                                          <Box sx={{
                                            width: 15,
                                            height: 15,
                                            borderRadius: '50%',
                                            border: '2px solid',
                                            borderColor: 'grey.300',
                                            flexShrink: 0,
                                            mt: '2px',
                                          }} />
                                        )}
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                            <Typography
                                              variant='caption'
                                              sx={{
                                                fontWeight: 500,
                                                color: stepDone ? 'text.secondary' : 'text.primary',
                                              }}
                                            >
                                              {step.name || step.label || getHumanReadableLabel(step.id)}
                                            </Typography>
                                            {step.required === false ? (
                                              <Typography component='span' sx={{
                                                fontSize: 9,
                                                fontWeight: 700,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.4,
                                                color: 'text.disabled',
                                                bgcolor: 'action.hover',
                                                borderRadius: 0.75,
                                                px: 0.75,
                                                py: 0.25,
                                                lineHeight: 1.2,
                                              }}>
                                                Optional
                                              </Typography>
                                            ) : null}
                                          </Box>

                                          {stepDone && typeof value === 'string' && value.length > 0 ? (
                                            <Typography
                                              variant='caption'
                                              sx={{
                                                display: 'inline-block',
                                                mt: 0.5,
                                                fontFamily: '"JetBrains Mono", "Menlo", "Consolas", monospace',
                                                fontSize: 11,
                                                letterSpacing: 0.4,
                                                bgcolor: 'action.hover',
                                                px: 0.75,
                                                py: 0.25,
                                                borderRadius: 1,
                                                wordBreak: 'break-all',
                                              }}
                                            >
                                              {value}
                                            </Typography>
                                          ) : null}
                                        </Box>
                                      </Box>
                                    );
                                  })}
                                </Box>
                              ) : null}
                            </Box>
                          );
                        })() : null}
                      </Box>
                    );
                  })}
                </Box>

                {/* Orphaned photos: damages + any step_id not in the current workflow */}
                {orphanedByStepId.size > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    {Array.from(orphanedByStepId.entries()).map(([stepId, photos]) => {
                      const label = stepId === 'damages' ? 'Damages' : getHumanReadableLabel(stepId);
                      return (
                        <Box key={stepId} sx={{ py: 0.75, px: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          <BiCheck size={15} color={theme.palette.grey[400]} style={{ marginTop: 2, flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant='caption' sx={{ fontWeight: 500, color: 'text.secondary' }}>
                              {label}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mt: 0.5 }}>
                              {photos.filter(p => p.url).map((photo, pi) => (
                                <Box
                                  key={photo.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openLightbox(photos, pi);
                                  }}
                                  sx={{
                                    width: '100%',
                                    aspectRatio: '4 / 3',
                                    borderRadius: 1.5,
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    boxShadow: '0px 2px 12px 2px rgba(32,36,48,0.09)',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0px 4px 16px 2px rgba(32,36,48,0.19)',
                                    },
                                  }}
                                >
                                  <img
                                    src={photo.url!}
                                    alt={photo.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                  />
                                </Box>
                              ))}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                ) : null}
              </Box>
              <Divider />
            </>
          ) : null}

          {/* ── Empty state for dealership stops ── */}
          {isDealershipStop ? (
            <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                {stop.stop_order === 1
                  ? 'This is the starting point for the run. The driver departs from the dealership.'
                  : 'This is the return point. The driver returns to the dealership after completing all stops.'
                }
              </Typography>
            </Box>
          ) : null}

          {/* ── Empty state for no data ── */}
          {!isDealershipStop && orders.length === 0 && allPhotos.length === 0 && timelineEvents.length === 0 ? (
            <Box sx={{ px: 2.5, py: 4, textAlign: 'center' }}>
              <Typography variant='body2' color='text.secondary'>
                No service data captured yet for this stop.
              </Typography>
            </Box>
          ) : null}

          {/* ── Skip info ── */}
          {stop.status === 'skipped' && detail ? (
            <Box sx={{ px: 2.5, py: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  bgcolor: theme.palette.error.light + '20',
                  border: 1,
                  borderColor: theme.palette.error.light + '60',
                  borderRadius: 1,
                }}>
                <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'error.dark' }}>
                  Stop Skipped
                </Typography>
                {detail.skip_reason_code ? (
                  <Typography variant='caption' color='error.dark' sx={{ display: 'block' }}>
                    Reason: {formatStatus(detail.skip_reason_code)}
                  </Typography>
                ) : null}
                {detail.skip_notes ? (
                  <Typography variant='caption' color='error.dark' sx={{ display: 'block', mt: 0.5 }}>
                    {detail.skip_notes}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </Box>
      </Drawer>

      <PhotoLightbox
        open={lightboxOpen}
        photos={lightboxPhotos}
        initialIndex={lightboxIndex}
        onClose={() => setLightboxOpen(false)}
      />
    </>
  );
};

export default StopDetailDrawer;
